import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureExceptionToSentry } from '../lib/sentry';
import { getLocale } from '../i18n';

/**
 * Wave 310 (extends Wave 36) — global + scoped React error boundary.
 *
 * Two boundary shapes share this class:
 *
 *   - <ErrorBoundary level="app">      → tree-wide last resort. Fullscreen
 *                                         pt-BR recovery card.
 *   - <ErrorBoundary level="route">    → per-route. Same fullscreen card
 *                                         but the rest of the shell
 *                                         (TopBar / sidebar) keeps
 *                                         working when the route remounts
 *                                         after the user clicks Reload.
 *   - <FeatureBoundary name="X">       → in-place inline fallback for
 *                                         risky widgets (3D scene, IFC
 *                                         viewer, PDF viewer, etc.). The
 *                                         rest of the page keeps
 *                                         rendering. See helper export
 *                                         below.
 *
 * Reporting: every catch fires a fire-and-forget POST to
 * /v1/client-errors with the message, normalized stack, route, build
 * id, and feature name. The endpoint dedups by fingerprint, so the
 * same crash from 200 users still shows up as one row in triage.
 *
 * No-throw guarantee: the report POST is wrapped in try/catch and the
 * fetch failure is swallowed silently. The error boundary's whole job
 * is to recover from a crash — it can't be the thing that crashes.
 */

type Level = 'app' | 'route' | 'feature';

interface Props {
  children: ReactNode;
  /** 'app' = fullscreen | 'route' = fullscreen | 'feature' = inline */
  level?: Level;
  /** Surface label sent to /v1/client-errors. e.g. "IfcViewer". */
  name?: string;
  /** Optional inline fallback — overrides the default for level=feature. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Build id: read once from the DOM. Vite injects this at build time so we
// can correlate occurrences with a specific deploy. Falls back to 'dev'.
const APP_VERSION = (() => {
  if (typeof document === 'undefined') return 'dev';
  return document.documentElement.getAttribute('data-build-id') ?? 'dev';
})();

async function postReport(payload: {
  message: string;
  stack?: string;
  level?: Level | 'global';
  feature?: string;
  route?: string;
}) {
  // Read auth defensively — never let storage failure block the fetch.
  let auth: string | null = null;
  try {
    auth = localStorage.getItem('builder.auth.token');
  } catch {
    // Storage unavailable (test env, locked-down browser, Safari ITP);
    // proceed without auth — the report must still be attempted.
    auth = null;
  }

  const apiBase =
    (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env
      ?.VITE_API_BASE ?? '';
  const url = `${apiBase}/v1/client-errors`;

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(auth ? { authorization: `Bearer ${auth}` } : {}),
      },
      body: JSON.stringify({
        message: payload.message,
        stack: payload.stack,
        level: payload.level,
        feature: payload.feature,
        route: payload.route ?? (typeof window !== 'undefined' ? window.location.pathname : null),
        appVersion: APP_VERSION,
      }),
      // Fire-and-forget — never block the recovery UI on the report.
      keepalive: true,
    });
  } catch {
    /* swallow — the boundary cannot itself throw */
  }
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged for Sentry's window.onerror hook to catch, and for the
    // devtools console in dev.
    console.error('[ErrorBoundary]', this.props.name ?? 'unnamed', error, info.componentStack);
    // Wave 310 mirror to /v1/client-errors. The Sentry hook already
    // captures via window.onerror, but render crashes don't always
    // bubble to that — React swallows them after dispatching to the
    // boundary. So this POST is the canonical signal for the autonomy
    // pipeline.
    void postReport({
      message: error.message || 'unknown_render_error',
      stack: error.stack ?? info.componentStack ?? undefined,
      level: this.props.level ?? 'app',
      feature: this.props.name,
    });
    // Error tracking · 2026-05-20 — also ship to the CF-native
    // observability firehose via the `sentry.ts` bridge. The bridge's
    // primary channel is `/v1/observability/client-error` (Builder
    // API), not Sentry SaaS — see `docs/ERROR-TRACKING-CF.md`. When
    // a real `VITE_SENTRY_DSN` is configured the bridge also fan-outs
    // to Sentry as a secondary channel; otherwise the call is just a
    // duplicate report to Builder API (cheap, dedup'd downstream).
    void captureExceptionToSentry(error, {
      level: this.props.level ?? 'app',
      feature: this.props.name,
      buildId: APP_VERSION,
    });
  }

  reset = () => {
    // Full reload — cheaper than trying to surgically unwind state when
    // we don't know what blew up.
    window.location.reload();
  };

  resetSoft = () => {
    // For feature-level boundaries, allow an in-place retry without
    // tearing down the whole page. Caller can also re-mount by changing
    // a `key` prop on the boundary.
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;
    const level: Level = this.props.level ?? 'app';

    const isEn = getLocale() === 'en';

    // ────── feature-level: small inline fallback ──────
    if (level === 'feature') {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '0.875rem 1rem',
            border: '1px solid rgb(var(--line, 229 231 235))',
            borderRadius: 10,
            background: 'rgb(var(--surface-2, 249 250 251))',
            color: 'rgb(var(--ink-muted, 75 85 99))',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontWeight: 600, color: 'rgb(var(--ink, 17 24 39))' }}>
            {this.props.name
              ? isEn ? `${this.props.name} unavailable` : `${this.props.name} indisponível`
              : isEn ? 'Component unavailable' : 'Componente indisponível'}
          </div>
          <div>
            {isEn
              ? 'Something crashed in this part of the screen. The rest of the page keeps working — try reloading just this component.'
              : 'Algo travou nessa parte da tela. O resto da página continua funcionando — tente recarregar só esse componente.'}
          </div>
          <div>
            <button
              type="button"
              onClick={this.resetSoft}
              style={{
                marginTop: 4,
                height: 32,
                padding: '0 12px',
                border: '1px solid rgb(var(--line, 229 231 235))',
                borderRadius: 6,
                background: 'rgb(var(--surface, 255 255 255))',
                color: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {isEn ? 'Try again' : 'Tentar de novo'}
            </button>
          </div>
        </div>
      );
    }

    // ────── app- or route-level: fullscreen recovery card ──────
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          background: 'rgb(var(--surface, 255 255 255))',
          color: 'rgb(var(--ink, 17 24 39))',
          fontFamily:
            'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: '100%',
            padding: '1.5rem',
            border: '1px solid rgb(var(--line, 229 231 235))',
            borderRadius: 12,
            background: 'rgb(var(--surface-2, 249 250 251))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {isEn ? 'Something went wrong' : 'Algo deu errado'}
          </h1>
          <p
            style={{
              marginTop: 8,
              fontSize: 14,
              lineHeight: 1.5,
              color: 'rgb(var(--ink-muted, 75 85 99))',
            }}
          >
            {isEn
              ? 'The screen crashed on our end. Your data is saved — just reload the page to continue.'
              : 'A tela travou do nosso lado. Seus dados estão salvos — é só recarregar a página para continuar.'}
          </p>
          {this.state.error?.message && (
            <pre
              style={{
                marginTop: 12,
                padding: '0.5rem 0.75rem',
                fontSize: 12,
                lineHeight: 1.4,
                color: 'rgb(var(--ink-subtle, 107 114 128))',
                background: 'rgb(var(--surface-3, 243 244 246))',
                border: '1px solid rgb(var(--line, 229 231 235))',
                borderRadius: 8,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 120,
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={this.reset}
              style={{
                flex: 1,
                height: 40,
                border: 'none',
                borderRadius: 8,
                background: 'rgb(var(--brand-500, 37 99 235))',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isEn ? 'Reload' : 'Recarregar'}
            </button>
            <a
              href="/"
              style={{
                flex: 1,
                height: 40,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgb(var(--line, 229 231 235))',
                borderRadius: 8,
                background: 'transparent',
                color: 'inherit',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              {isEn ? 'Home page' : 'Página inicial'}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Convenience wrapper for inline fallbacks. Use around 3D scenes, IFC
 * viewer, PDF generators, anything that can throw on a corrupt input
 * but where the rest of the page should keep working.
 */
export function FeatureBoundary({
  children,
  name,
  fallback,
}: {
  children: ReactNode;
  name: string;
  fallback?: ReactNode;
}) {
  return (
    <ErrorBoundary level="feature" name={name} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Wave 310 — global window.onerror + unhandledrejection capture.
 *
 * React error boundaries only see *render* errors. Async errors thrown
 * inside event handlers, promise rejections, and setTimeout callbacks
 * never reach a boundary. This installs the missing surface so they
 * also flow into /v1/client-errors and into the same fingerprint
 * pipeline.
 *
 * Call once at app startup (main.tsx). Idempotent — guards against
 * duplicate listener install across HMR reloads in dev.
 */
declare global {
  interface Window {
    __builderErrorHooksInstalled?: boolean;
  }
}

export function installGlobalErrorHooks(): void {
  if (typeof window === 'undefined') return;
  if (window.__builderErrorHooksInstalled) return;
  window.__builderErrorHooksInstalled = true;

  window.addEventListener('error', (e) => {
    if (!e.error && !e.message) return;
    void postReport({
      message:
        (e.error instanceof Error ? e.error.message : null) ??
        e.message ??
        'unknown_window_error',
      stack: e.error instanceof Error ? e.error.stack : undefined,
      level: 'global',
    });
    const errLike =
      e.error instanceof Error
        ? e.error
        : new Error(e.message ?? 'unknown_window_error');
    void captureExceptionToSentry(errLike, { level: 'global', buildId: APP_VERSION });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'unhandled_rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;
    void postReport({ message, stack, level: 'global' });
    const errLike = reason instanceof Error ? reason : new Error(message);
    void captureExceptionToSentry(errLike, { level: 'global', buildId: APP_VERSION });
  });
}
